/**
 * Tiny, dependency-free Markdown → safe-HTML renderer for assistant replies.
 *
 * The output is injected with Lit's `unsafeHTML`, and our components render into
 * light DOM, so safety is non-negotiable: we HTML-escape every piece of source
 * text *before* wrapping it in tags, and only ever emit a fixed set of tags.
 * The sole place untrusted text reaches an attribute is a link `href`, which is
 * scheme-checked (http/https/mailto/relative only) — anything else degrades to
 * plain text. This covers the Markdown an LLM actually emits (headings, lists,
 * emphasis, code, links, quotes, rules); it is deliberately not a full CommonMark
 * implementation.
 */

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Only allow link targets that can't execute script. */
const safeHref = (url: string): string | null =>
  /^(https?:\/\/|mailto:|\/|#)/i.test(url.trim()) ? url.trim() : null;

/**
 * Inline formatting on a line of *already HTML-escaped* text: code spans,
 * bold, italic, and links. Code spans are extracted to placeholders first so
 * their contents aren't re-formatted (e.g. `**x**` inside backticks stays
 * literal).
 */
function inline(escaped: string): string {
  const codes: string[] = [];
  let text = escaped.replace(/`([^`]+)`/g, (_m, code: string) => {
    codes.push(`<code>${code}</code>`);
    return ` ${codes.length - 1} `;
  });

  // Links: [text](url) — validate the scheme or fall back to the raw text.
  text = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, url: string) => {
    const href = safeHref(url);
    return href
      ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${label}</a>`
      : label;
  });

  text = text
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>')
    .replace(/(^|[^_\w])_([^_\s][^_]*)_/g, '$1<em>$2</em>');

  // Restore code spans.
  return text.replace(/ (\d+) /g, (_m, i: string) => codes[Number(i)] ?? '');
}

const HEADING = /^(#{1,6})\s+(.*)$/;
const HR = /^(?:---|\*\*\*|___)\s*$/;
const UL = /^[-*+]\s+(.*)$/;
const OL = /^(\d+)[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;

/** Render Markdown source to a safe HTML string. */
export function renderMarkdown(src: string): string {
  const lines = (src ?? '').replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;

  const flushList = (tag: 'ul' | 'ol', items: string[]) => {
    out.push(`<${tag}>${items.map((it) => `<li>${inline(it)}</li>`).join('')}</${tag}>`);
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // Fenced code block — emit verbatim (escaped), no inline formatting.
    const fence = line.match(/^```(.*)$/);
    if (fence) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i] ?? '')) {
        body.push(lines[i] ?? '');
        i++;
      }
      i++; // consume closing fence
      out.push(`<pre><code>${escapeHtml(body.join('\n'))}</code></pre>`);
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    if (HR.test(line)) {
      out.push('<hr>');
      i++;
      continue;
    }

    const h = line.match(HEADING);
    if (h) {
      const level = h[1]!.length;
      out.push(`<h${level}>${inline(escapeHtml(h[2]!))}</h${level}>`);
      i++;
      continue;
    }

    // Unordered list — consume consecutive items.
    if (UL.test(line)) {
      const items: string[] = [];
      while (i < lines.length && UL.test(lines[i] ?? '')) {
        items.push(escapeHtml((lines[i]!.match(UL)![1] ?? '')));
        i++;
      }
      flushList('ul', items);
      continue;
    }

    // Ordered list.
    if (OL.test(line)) {
      const items: string[] = [];
      while (i < lines.length && OL.test(lines[i] ?? '')) {
        items.push(escapeHtml((lines[i]!.match(OL)![2] ?? '')));
        i++;
      }
      flushList('ol', items);
      continue;
    }

    // Blockquote.
    if (QUOTE.test(line)) {
      const quoted: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] ?? '')) {
        quoted.push(escapeHtml((lines[i]!.match(QUOTE)![1] ?? '')));
        i++;
      }
      out.push(`<blockquote>${quoted.map((q) => inline(q)).join('<br>')}</blockquote>`);
      continue;
    }

    // Paragraph — gather consecutive plain lines, soft-breaking on newlines.
    const para: string[] = [];
    while (
      i < lines.length &&
      (lines[i] ?? '').trim() !== '' &&
      !HR.test(lines[i] ?? '') &&
      !HEADING.test(lines[i] ?? '') &&
      !UL.test(lines[i] ?? '') &&
      !OL.test(lines[i] ?? '') &&
      !QUOTE.test(lines[i] ?? '') &&
      !/^```/.test(lines[i] ?? '')
    ) {
      para.push(inline(escapeHtml(lines[i] ?? '')));
      i++;
    }
    out.push(`<p>${para.join('<br>')}</p>`);
  }

  return out.join('');
}
